import React, { useState, useEffect } from 'react';
import { memberService } from '../../services/member.service';
import { Member } from '../../types';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Loading } from '../../components/ui/Loading';
import { formatDate } from '../../utils/formatters';
import { toast } from 'react-toastify';

export const ProfilePage: React.FC = () => {
  const [member, setMember] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    emergencyContacts: ''
  });

  useEffect(() => {
    loadProfile();

    const interval = setInterval(() => {
      loadProfile(true);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadProfile = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const data = await memberService.getMemberProfile();
      setMember(data);
      // Only update form data if not editing, to avoid overwriting user input
      if (!isEditing) {
        setFormData({
          name: data.name,
          phone: data.phone,
          emergencyContacts: data.emergencyContacts
        });
      }
    } catch (error) {
      if (!silent) toast.error('Failed to load profile');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await memberService.updateMemberProfile(formData);
      toast.success('Profile updated successfully');
      setIsEditing(false);
      loadProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loading size="lg" text="Loading profile..." />
      </div>
    );
  }

  if (!member) return null;

  const getStatusBadge = (status: string) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      EXPIRED: 'bg-red-100 text-red-800',
      SUSPENDED: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-600">Manage your membership information</p>
      </div>

      {/* Membership Status Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Membership Status</h2>
            <p className="text-sm text-gray-600">ID: {member.membershipId}</p>
          </div>
          <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(member.membershipStatus)}`}>
            {member.membershipStatus}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Type</label>
            <p className="text-gray-900">{member.membershipType}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Valid Until</label>
            <p className="text-gray-900">{formatDate(member.validUntil)}</p>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)}>Edit Profile</Button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
            />
            <Input
              label="Emergency Contacts (JSON)"
              value={formData.emergencyContacts}
              onChange={(e) => setFormData({ ...formData, emergencyContacts: e.target.value })}
            />
            <div className="flex space-x-2">
              <Button type="submit">Save Changes</Button>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Name</label>
              <p className="text-gray-900">{member.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="text-gray-900">{member.phone}</p>
            </div>
          </div>
        )}
      </div>

      {/* Family Members */}
      {member.familyMembers && member.familyMembers.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Family Members</h2>
          <div className="space-y-3">
            {member.familyMembers.map((fm) => (
              <div key={fm.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{fm.name}</p>
                    <p className="text-sm text-gray-600">{fm.relationship}</p>
                  </div>
                  {fm.phone && <p className="text-sm text-gray-600">{fm.phone}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
